import { useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { selectClient, selectFeatureFlags } from '@client/model';
import { clientNeedsMedicalQuestionnaire } from '@/shared/lib/utils';
import { FundingPolicyType, IntakeType } from '@shared';
import { analytics } from '@shared/posthog';
import { useCheckEligibility } from './use-check-eligibility';
import { useUnderwritingResults } from './use-underwriting-results';
import { getUnderwritingResultForProduct } from '../utils/get-underwriting-result-for-product';

interface UseUnderwritingStatusOptions {
  productName: string;
  fundingPolicyType: FundingPolicyType;
  policyType?: string;
}

export function useUnderwritingStatus({
  productName,
  fundingPolicyType,
  policyType,
}: UseUnderwritingStatusOptions) {
  const client = useSelector(selectClient);
  const clientId = client?.clientId;
  const featureFlags = useSelector(selectFeatureFlags);

  const {
    handleCheckEligibility,
    isLoading: isCheckingEligibility,
    hasFailed: hasCheckFailed,
    partnerIntakeType,
  } = useCheckEligibility();
  const { data: underwritingData, refetch: refetchUnderwritingResults } =
    useUnderwritingResults(clientId);
  const [isRechecking, setIsRechecking] = useState(false);

  // State for dialogs
  const [showSlideOut, setShowSlideOut] = useState(false);
  const [showQuestionnaireDialog, setShowQuestionnaireDialog] = useState(false);
  const [questionnaireVariant, setQuestionnaireVariant] = useState<
    'underwriting' | 'partner-underwriting'
  >('underwriting');
  const [questionnaireClient, setQuestionnaireClient] = useState<Client | undefined>(undefined);

  // Determine if this is a joint policy
  const isJointPolicy = policyType === 'Joint Life';

  // The label has to promise what the click will do. For the client that is the banner's own
  // rule, so the two never disagree. A joint policy also needs the partner's questionnaire, and a
  // click then opens theirs, so a partner without one unlocks rather than checks.
  const partnerNeedsQuestionnaire =
    isJointPolicy &&
    !!client?.mutableClientPartner &&
    (partnerIntakeType === IntakeType.SHORT_FORM || partnerIntakeType === IntakeType.NO_INTAKE);
  const needsMedicalQuestionnaire =
    clientNeedsMedicalQuestionnaire(client ?? null, !!featureFlags?.genworthMode) ||
    partnerNeedsQuestionnaire;

  // Get the underwriting result for this specific product
  const underwritingResult = getUnderwritingResultForProduct(
    productName,
    underwritingData?.results,
    isJointPolicy,
    fundingPolicyType,
  );

  const status = underwritingResult?.status;

  // Handler to show questionnaire dialog
  const handleShowQuestionnaireDialog = useCallback(
    (variant: 'underwriting' | 'partner-underwriting') => {
      setQuestionnaireVariant(variant);
      // If partner variant, pass the partner as the client
      if (variant === 'partner-underwriting' && client?.mutableClientPartner) {
        setQuestionnaireClient(client.mutableClientPartner as Client);
      } else {
        setQuestionnaireClient(client);
      }
      setShowQuestionnaireDialog(true);
    },
    [client],
  );

  // Handler to check eligibility
  const handleCheckEligibilityClick = useCallback(async () => {
    analytics.underwriting.checkEligibilityClicked({
      clientId,
      fundingPolicyType,
    });

    // "Couldn't complete" also covers a check we stopped waiting for, and that one may have
    // finished since. Look before starting a second analysis beside the first.
    if (hasCheckFailed) {
      setIsRechecking(true);
      try {
        const latest = await refetchUnderwritingResults();
        const arrived = getUnderwritingResultForProduct(
          productName,
          latest.data?.results,
          isJointPolicy,
          fundingPolicyType,
        );
        if (arrived) return;
      } finally {
        setIsRechecking(false);
      }
    }

    handleCheckEligibility({
      fundingPolicyType,
      policyType,
      onShowQuestionnaireDialog: handleShowQuestionnaireDialog,
    });
  }, [
    clientId,
    productName,
    fundingPolicyType,
    policyType,
    isJointPolicy,
    hasCheckFailed,
    refetchUnderwritingResults,
    handleCheckEligibility,
    handleShowQuestionnaireDialog,
  ]);

  // Handler to view details
  const handleViewDetails = useCallback(() => {
    if (underwritingResult) {
      analytics.underwriting.viewed({
        clientId,
        productName,
        fundingPolicyType,
      });

      setShowSlideOut(true);
    }
  }, [clientId, productName, fundingPolicyType, underwritingResult]);

  return {
    // Status data
    status,
    underwritingResult: underwritingResult?.resultsText ?? null,
    structuredResult: underwritingResult?.structuredResult ?? null,
    pipelineMetadata: underwritingResult?.pipelineMetadata ?? null,
    hasUnderwritingResult: !!underwritingResult,
    isSampleClient: !!client?.isSampleClient,

    // Loading state
    isLoading: isCheckingEligibility || isRechecking,
    /** The last check ended without a result, so the button offers another try. */
    hasCheckFailed,
    /** The analysis cannot run yet, and the button says what will unlock it. */
    needsMedicalQuestionnaire,

    // Dialog states
    showSlideOut,
    setShowSlideOut,
    showQuestionnaireDialog,
    setShowQuestionnaireDialog,
    questionnaireVariant,
    questionnaireClient,

    // Handlers
    handleCheckEligibilityClick,
    handleViewDetails,
  };
}
